// UserFeedItem.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import UserStatusIndicator from '../container/UserStatusIndicator.js';
import UserAvatar from '../container/UserAvatar.js';
import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { textColor } from '../style/variables.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import HighlightSubstring from '../component/HighlightSubstring.jsx';
import FollowButton from '../container/FollowButton.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';

const srcSet = [
  MediaAssetFormat.AVIF,
  MediaAssetFormat.WEBP,
  MediaAssetFormat.JPG,
]
  .map(format => {
    return [
      { size: 128, resolution: '1x' },
      { size: 256, resolution: '2x' },
    ].map(size => ({ ...size, ext: format }));
  })
  .flatMap(a => a);

class UserFeedItem extends React.PureComponent {
  componentDidMount() {
    const { id, username, fetchUser } = this.props;
    if (id && !username) {
      this.timeout = setTimeout(() => fetchUser({ id }), 0);
    }
  }
  componentWillUnmount() {
    clearTimeout(this.timeout);
  }
  renderUserInfo = () => {
    const { id, username, fontSize, marginTop, query } = this.props;
    return (
      <User fontSize={fontSize} marginTop={marginTop}>
        <Username>
          <UsernameText>
            {query ? (
              <HighlightSubstring
                substring={query}
              >{`@${username}`}</HighlightSubstring>
            ) : (
              `@${username}`
            )}
          </UsernameText>
          <UserStatusIndicatorWrapper>
            <UserStatusIndicator
              userId={id}
              shouldShowLiveStream
              livestreamSize={12}
              shouldShowOnline
            />
          </UserStatusIndicatorWrapper>
        </Username>
      </User>
    );
  };
  handleClickUser = args => {
    const { username, shouldTrackEvent, categoryId, categoryIndex } =
      this.props;
    if (shouldTrackEvent) {
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          buttonId: ButtonId.All.ButtonCpCard,
          extraProps: {
            'user.username': username,
            'discover.index': categoryIndex,
            'discover.category': categoryId,
            ...args,
          },
        },
      });
    }
  };
  render() {
    const {
      withChatLink,
      withHalo,
      size,
      id,
      username,
      pathname,
      sendChatStart,
      shouldUseFollowButton,
    } = this.props;
    return (
      <StyledUserFeedItem size={size}>
        {withChatLink ? (
          <UserBlock
            title={username}
            onClick={() => sendChatStart({ userId: id })}
          >
            <UserAvatar id={id} size={size} srcSet={srcSet} />
            {this.renderUserInfo()}
          </UserBlock>
        ) : withHalo ? (
          <UserBlock title={username}>
            <UserAvatarWithStatus
              id={id}
              size={size}
              srcSet={srcSet}
              pathname={pathname}
              shouldTrackEvent={false}
              onClick={this.handleClickUser}
            />
            <Link to={`/user/${id}`} title={username}>
              {this.renderUserInfo()}
            </Link>
            {shouldUseFollowButton ? (
              <FollowButtonWrapper>
                <FollowButton userId={id} isOnSearch color={'#191919'} />
              </FollowButtonWrapper>
            ) : null}
          </UserBlock>
        ) : (
          <Link to={`/user/${id}`} title={username}>
            <UserAvatar id={id} size={size} srcSet={srcSet} />
            {this.renderUserInfo()}
          </Link>
        )}
      </StyledUserFeedItem>
    );
  }
}

UserFeedItem.propTypes = {
  id: PropTypes.string,
  username: PropTypes.string,
  pathname: PropTypes.string,
  withHalo: PropTypes.bool,
  withChatLink: PropTypes.bool,
  size: PropTypes.number,
  fontSize: PropTypes.number,
  marginTop: PropTypes.number,
  fetchUser: PropTypes.func,
  sendChatStart: PropTypes.func,
  shouldTrackEvent: PropTypes.bool,
  categoryId: PropTypes.string,
  categoryIndex: PropTypes.number,
  query: PropTypes.string,
  shouldUseFollowButton: PropTypes.bool,
};

UserFeedItem.defaultProps = {
  id: '',
  username: '',
  matchedString: '',
  pathname: '',
  withHalo: false,
  withChatLink: false,
  size: 88,
  fontSize: 12,
  marginTop: 16,
  fetchUser: () => null,
  sendChatStart: () => null,
  shouldTrackEvent: false,
  categoryId: '',
  categoryIndex: null,
  query: null,
  shouldUseFollowButton: false,
};

const StyledUserFeedItem = styled.section`
  width: ${({ size }) => `${size}px`};
  margin: 0 auto;
`;

const UserBlock = styled.div`
  display: block;
  text-align: center;
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }
`;

const User = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ fontSize }) => `${fontSize}px`};
  margin-top: ${({ marginTop }) => `${marginTop}px`};
`;

const Username = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 16px;
`;

const UsernameText = styled.div`
  display: inline-block;
  color: ${textColor.white};
  ${TextEllipsis};
  line-height: 1.4;
`;

const UserStatusIndicatorWrapper = styled.div`
  display: inline-block;
  margin-left: 4px;
`;

const FollowButtonWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: center;
`;

export default UserFeedItem;
